import React, { useCallback, useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { getAPIBaseURL } from "../../services/helpers"
import { deleteData, getData } from "../../services/request"
import {
  apiErrorMessage,
  batchStatusClass,
  batchStatusLabel,
  formatScore,
} from "./cvScreeningUtils"

const CvScreeningListPage = () => {
  const [batches, setBatches] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [deletingId, setDeletingId] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError("")
    const url = `${getAPIBaseURL()}/employme/employer/cv-screening`
    const res = await getData(url, {}, {})
    setLoading(false)
    if (res.error === "" && res.result?.status === 200) {
      const payload = res.result.data?.data || {}
      setBatches(payload.batches || [])
    } else {
      setError(apiErrorMessage(res, "Could not load screening batches"))
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const handleDelete = async (batch) => {
    if (!window.confirm("Delete this screening batch? Reports will no longer be available.")) {
      return
    }
    setDeletingId(batch._id)
    const url = `${getAPIBaseURL()}/employme/employer/cv-screening/${batch._id}`
    const res = await deleteData(url, {}, {})
    setDeletingId(null)
    if (res.error === "" && res.result?.status === 200) {
      await load()
    } else {
      setError(apiErrorMessage(res, "Could not delete batch"))
    }
  }

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleString()
    } catch (e) {
      return iso
    }
  }

  return (
    <div className="mx-auto max-w-6xl animate-fade-in">
      <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-900 via-slate-800 to-cyan-900 px-5 py-8 text-white shadow-sm sm:px-8 sm:py-10">
        <div
          className="pointer-events-none absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 20%, rgba(0,165,230,0.45), transparent 45%), radial-gradient(circle at 80% 0%, rgba(255,255,255,0.12), transparent 40%)",
          }}
        />
        <div className="relative flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-cyan-200/90">
              CV Screening
            </p>
            <h2 className="mt-2 text-2xl font-semibold tracking-tight sm:text-3xl">
              Screen CVs with AI
            </h2>
            <p className="mt-2 max-w-xl text-sm text-slate-200/90">
              Attach a live job or define a role, upload a batch of CVs, and get scored reports against the metrics you choose.
            </p>
          </div>
          <Link
            to="/employer/cv-screening/new"
            className="inline-flex items-center justify-center rounded-xl bg-white px-5 py-2.5 text-sm font-semibold text-slate-900 shadow-sm transition hover:bg-cyan-50"
          >
            New screening
          </Link>
        </div>
      </div>

      <div className="mt-6">
        {loading ? (
          <div className="rounded-xl border border-gray-200 bg-white p-10 text-center">
            <div className="mx-auto mb-3 h-8 w-8 animate-pulse-soft rounded-full bg-slate-200" />
            <p className="text-sm text-gray-500">Loading batches...</p>
          </div>
        ) : error ? (
          <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {error}
          </div>
        ) : batches.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-300 bg-white px-6 py-14 text-center animate-slide-up">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-cyan-50 text-cyan-600">
              <svg className="h-7 w-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.75}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <h3 className="text-base font-semibold text-gray-900">No screenings yet</h3>
            <p className="mx-auto mt-1 max-w-sm text-sm text-gray-500">
              Upload a set of CVs against a role to generate individual and batch AI reports.
            </p>
            <Link
              to="/employer/cv-screening/new"
              className="mt-5 inline-flex rounded-lg bg-black px-4 py-2 text-sm font-semibold text-white hover:bg-gray-900"
            >
              Start your first screening
            </Link>
          </div>
        ) : (
          <ul className="space-y-3">
            {batches.map((batch, idx) => {
              const progress = batch.progress || {}
              const pct =
                progress.total > 0
                  ? Math.round((progress.processed / progress.total) * 100)
                  : 0
              return (
                <li
                  key={batch._id}
                  className="group rounded-xl border border-gray-200 bg-white p-4 shadow-sm transition hover:border-cyan-200 hover:shadow-md animate-slide-up sm:p-5"
                  style={{ animationDelay: `${Math.min(idx, 8) * 40}ms` }}
                >
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="truncate text-base font-semibold text-gray-900">
                          {batch.roleSnapshot?.title || "Untitled role"}
                        </h3>
                        <span
                          className={`inline-flex rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${batchStatusClass(
                            batch.status
                          )}`}
                        >
                          {batchStatusLabel(batch.status)}
                        </span>
                        <span className="text-[11px] text-gray-400">
                          {batch.sourceType === "JOB_POST" ? "From live job" : "Screening role"}
                        </span>
                      </div>
                      <p className="mt-1 text-xs text-gray-500">
                        {formatDate(batch.createdAt)} · {progress.total || 0} CVs
                        {typeof batch.summaryReport?.averageOverallScore === "number"
                          ? ` · Avg score ${formatScore(batch.summaryReport.averageOverallScore)}`
                          : ""}
                      </p>
                      {(batch.status === "PROCESSING" || batch.status === "QUEUED") &&
                      progress.total > 0 ? (
                        <div className="mt-3">
                          <div className="mb-1 flex justify-between text-[11px] text-gray-500">
                            <span>
                              {progress.processed}/{progress.total} processed
                            </span>
                            <span>{pct}%</span>
                          </div>
                          <div className="h-1.5 overflow-hidden rounded-full bg-slate-100">
                            <div
                              className="h-full rounded-full bg-cyan-500 transition-all duration-500"
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                        </div>
                      ) : null}
                    </div>
                    <div className="flex shrink-0 flex-wrap gap-2">
                      <Link
                        to={`/employer/cv-screening/${batch._id}`}
                        className="rounded-lg bg-black px-3 py-1.5 text-xs font-semibold text-white hover:bg-gray-900"
                      >
                        Open
                      </Link>
                      {!["PROCESSING", "QUEUED"].includes(batch.status) ? (
                        <button
                          type="button"
                          onClick={() => handleDelete(batch)}
                          disabled={deletingId === batch._id}
                          className="rounded-lg border border-red-200 px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
                        >
                          {deletingId === batch._id ? "..." : "Delete"}
                        </button>
                      ) : null}
                    </div>
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </div>
  )
}

export default CvScreeningListPage
